<template>
  <ChartCard :data-widget-id="isTableGenderData ? 'gender-data-table' : 'race-data-table'">
    <ChartHeader
      label="Demographic Data Table"
      tooltip-key="demo-survey-data-table"
      include-csv-download-option
      :disabled="downloading"
      :widget-download-id="isTableGenderData ? 'gender-data-table' : 'race-data-table'"
      @download-csv="handleCSVDownload"
    >
      <template #appendTitle>
        <DpplSwitch
          data-png-ignore
          left="Gender data"
          right="Race data"
          :value="isTableGenderData"
          :width="176"
          new-version
          class="slim-switch ml-2"
          @change="handleUpdateIsTableGenderData"
        />
      </template>
    </ChartHeader>
    <InsightsTable
      :rows="genderAndRaceTableRows"
      :totals="tableTotals"
      :headers="tableHeaders"
      class="pa-4 pt-0"
      show-passthrough-header
    />
  </ChartCard>
</template>

<script setup>
import ChartCard from '@/components/insights/Chart/ChartCard.vue';
import ChartHeader from '@/components/insights/Chart/ChartHeader.vue';
import DpplSwitch from '@/components/datapeople/DpplSwitch.vue';
import { useDemographicSurveyReport } from '@/stores/insights/demographicSurveyReport';
import { storeToRefs } from 'pinia';
import { useTracking } from '@/stores/tracking/tracking';
import InsightsTable from '@/components/insights/table/InsightsTable.vue';
import { computed, ref } from 'vue';
import { useAppAuthData } from '@/stores/app/authData';
import { useDatePicker } from '@/stores/filters/datePicker';

const { updateIsTableGenderData, downloadGenderOrRaceTableCSV } = useDemographicSurveyReport();
const {
  genderAndRaceTableRows,
  isTableGenderData,
  genderAndRaceTableTotals,
} = storeToRefs(useDemographicSurveyReport());
const { authData } = storeToRefs(useAppAuthData());
const { projectName } = authData.value;
const { dateRange } = storeToRefs(useDatePicker());

const tableHeaders = [
  'Applied',
  'Screening',
  'Assessment',
  'Offer',
  'Hired',
];

const downloading = ref(false);

const tableTotals = computed(() => [
  {
    id: 'applied',
    value: genderAndRaceTableTotals.value.review.value,
    percent: genderAndRaceTableTotals.value.review.percent,
    isPassthrough: true,
  },
  {
    id: 'screening',
    value: genderAndRaceTableTotals.value.screen.value,
    percent: genderAndRaceTableTotals.value.screen.percent,
    isPassthrough: true,
  },
  {
    id: 'assessment',
    value: genderAndRaceTableTotals.value.assessment.value,
    percent: genderAndRaceTableTotals.value.assessment.percent,
    isPassthrough: true,
  },
  {
    id: 'offer',
    value: genderAndRaceTableTotals.value.offer.value,
    percent: genderAndRaceTableTotals.value.offer.percent,
    isPassthrough: true,
  },
  {
    id: 'hired',
    value: genderAndRaceTableTotals.value.hired.value,
  },
]);

const handleUpdateIsTableGenderData = (value) => {
  if (!value) {
    useTracking().trackName({
      name: 'Viewed Race Data table on Demographic-survey-report page',
    });
  }

  updateIsTableGenderData(value);
};

const handleCSVDownload = async () => {
  downloading.value = true;

  await downloadGenderOrRaceTableCSV(
    projectName,
    dateRange.value,
    isTableGenderData.value,
  );

  downloading.value = false;
};
</script>

<style scoped lang="less">
.datapeople-app .slim-switch {
  height: 31px !important;
  border: 0.5px solid @stroke-default;

  :deep(.button-background-container) {
    padding: 4px !important;
  }

  :deep(.p2) {
    font-size: 12px !important;

    &.button-background {
      height: 23px !important;
    }
  }
}
</style>
